<template>
  <nav class="sidebar-nav" :class="{ collapsed: isCollapsed }">
    <div class="sidebar-header">
      <div class="brand">
        <div class="logo">✨</div>
        <h2 v-if="!isCollapsed">Astro Way</h2>
      </div>
      <button class="collapse-btn" @click="toggleCollapse" :aria-label="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'">
        <nav-icon :name="isCollapsed ? 'chevronRight' : 'chevronLeft'" :size="16" />
      </button>
    </div>

    <!-- Search (expanded only) -->
    <div v-if="!isCollapsed" class="nav-search">
      <nav-icon name="search" :size="15" class="search-ic" />
      <input v-model="query" type="text" placeholder="Search pages…" aria-label="Search pages" />
      <button v-if="query" class="clear-btn" @click="clearQuery" aria-label="Clear search"><nav-icon name="close" :size="13" /></button>
    </div>

    <!-- Expanded: grouped accordion -->
    <div v-if="!isCollapsed" class="nav-scroll">
      <div v-for="group in filteredGroups" :key="group.id" class="nav-group">
        <button class="group-header" @click="toggleGroup(group.id)" :aria-expanded="isGroupOpen(group.id)">
          <nav-icon :name="group.icon" :size="15" class="group-ic" />
          <span class="group-label">{{ group.label }}</span>
          <nav-icon name="chevron" :size="14" class="group-caret" :class="{ open: isGroupOpen(group.id) }" />
        </button>
        <div class="group-items-wrap" :class="{ open: isGroupOpen(group.id) }">
          <div class="group-items">
            <router-link v-for="item in group.items" :key="item.to" :to="item.to" class="nav-link" :title="item.label">
              <nav-icon :name="item.icon" :size="17" />
              <span>{{ item.label }}</span>
            </router-link>
          </div>
        </div>
      </div>
      <p v-if="!filteredGroups.length" class="no-match">No pages match “{{ query }}”.</p>
    </div>

    <!-- Collapsed: flat icon rail with per-group dividers -->
    <div v-else class="nav-scroll collapsed-scroll">
      <template v-for="group in navGroups" :key="group.id">
        <div class="collapsed-divider" :title="group.label"><nav-icon :name="group.icon" :size="13" /></div>
        <router-link v-for="item in group.items" :key="item.to" :to="item.to" class="nav-link icon-only" :title="item.label">
          <nav-icon :name="item.icon" :size="18" />
        </router-link>
      </template>
    </div>
  </nav>
</template>

<script>
import { ref } from 'vue'
import NavIcon from './NavIcon.vue'
import { navGroups } from '../nav'
import { useNavGroups } from '../composables/useNavGroups'

export default {
  name: 'SidebarNav',
  components: { NavIcon },
  setup() {
    const isCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')
    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value
      try { localStorage.setItem('sidebarCollapsed', isCollapsed.value) } catch (e) { /* ignore */ }
    }

    const { query, filteredGroups, isGroupOpen, toggleGroup, clearQuery } = useNavGroups('sidebarOpenGroups')

    return { isCollapsed, toggleCollapse, navGroups, query, filteredGroups, isGroupOpen, toggleGroup, clearQuery }
  }
}
</script>

<style scoped>
.sidebar-nav {
  width: 232px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--background-dark);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  padding: 14px 10px;
  transition: width 0.2s ease;
  overflow: hidden;
}
.sidebar-nav.collapsed { width: 64px; padding: 14px 8px; }

/* Header */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 6px 14px;
  margin-bottom: 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
.logo { font-size: 22px; flex-shrink: 0; line-height: 1; }
.sidebar-header h2 {
  font-size: 16px; margin: 0; font-weight: 600; color: var(--text-primary); white-space: nowrap;
}
.collapse-btn {
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  width: 28px; height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--text-secondary);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.collapse-btn:hover { color: var(--text-primary); background: rgba(108, 92, 231, 0.25); }
.collapsed .sidebar-header { justify-content: center; }
.collapsed .brand { justify-content: center; }

/* Search */
.nav-search {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.nav-search .search-ic { position: absolute; left: 10px; color: var(--text-secondary); pointer-events: none; }
.nav-search input {
  width: 100%;
  padding: 9px 30px 9px 32px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9px;
  color: var(--text-primary);
  font-size: 13px;
}
.nav-search input::placeholder { color: var(--text-secondary); }
.nav-search input:focus {
  outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 2px rgba(108, 92, 231, 0.2);
}
.clear-btn {
  position: absolute; right: 6px;
  display: flex; align-items: center; justify-content: center;
  width: 22px; height: 22px;
  background: none; border: none; color: var(--text-secondary); cursor: pointer; border-radius: 6px;
}
.clear-btn:hover { color: var(--text-primary); background: rgba(255, 255, 255, 0.08); }

/* Scroll area */
.nav-scroll { flex: 1; overflow-y: auto; overflow-x: hidden; padding-right: 2px; }
.nav-scroll::-webkit-scrollbar { width: 6px; }
.nav-scroll::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.12); border-radius: 3px; }

/* Group */
.nav-group { margin-bottom: 2px; }
.group-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  cursor: pointer;
  border-radius: 8px;
  transition: color 0.2s ease, background 0.2s ease;
}
.group-header:hover { color: var(--text-primary); background: rgba(255, 255, 255, 0.04); }
.group-ic { opacity: 0.8; }
.group-label { flex: 1; text-align: left; }
.group-caret { opacity: 0.6; transition: transform 0.2s ease; }
.group-caret.open { transform: rotate(180deg); }

/* Smooth height accordion via grid rows */
.group-items-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.22s ease;
}
.group-items-wrap.open { grid-template-rows: 1fr; }
.group-items { overflow: hidden; display: flex; flex-direction: column; gap: 2px; padding: 2px 0; }

/* Links */
.nav-link {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 9px 10px 9px 14px;
  border-radius: 9px;
  text-decoration: none;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.18s ease, background 0.18s ease;
}
.nav-link:hover { background: rgba(255, 255, 255, 0.06); color: var(--text-primary); }
.nav-link.router-link-exact-active {
  background: var(--primary-color);
  color: #fff;
}
.nav-link.router-link-exact-active :deep(.nav-icon) { color: #fff; }

.no-match { padding: 12px 10px; font-size: 12.5px; color: var(--text-secondary); }

/* Collapsed rail */
.collapsed-scroll { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.collapsed-divider {
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 26px;
  margin-top: 8px;
  color: var(--text-secondary);
  opacity: 0.5;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.collapsed-divider:first-child { border-top: none; margin-top: 0; }
.nav-link.icon-only {
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  gap: 0;
}

@media (max-width: 1024px) {
  .sidebar-nav { display: none; }
}
</style>
